import { useState, useEffect } from "react";
import { LightPageLayout } from "../components/Layout";
import { API } from "../App";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Search, ChevronDown, HelpCircle, Briefcase, DollarSign, Calendar, BookOpen, ClipboardList, Rocket } from "lucide-react";

const CATEGORY_ICONS = {
  "Eligibility & Who It's For": HelpCircle,
  "Cost & Funding": DollarSign,
  "Schedule & Format": Calendar,
  "Programs & Curriculum": BookOpen,
  "Enrollment & Application": ClipboardList,
  "Apprenticeship & Employment": Briefcase,
};

const CATEGORY_COLORS = {
  "Eligibility & Who It's For": "from-blue-500 to-blue-600",
  "Cost & Funding": "from-emerald-500 to-emerald-600",
  "Schedule & Format": "from-purple-500 to-purple-600",
  "Programs & Curriculum": "from-amber-500 to-amber-600",
  "Enrollment & Application": "from-[#FFD507] to-[#96700F]",
  "Apprenticeship & Employment": "from-[#1E3A8A] to-[#152C6E]",
};

const FAQPage = () => {
  const [faqs, setFaqs] = useState([]);
  const [search, setSearch] = useState("");
  const [openItems, setOpenItems] = useState({});
  const [openCategories, setOpenCategories] = useState({});

  useEffect(() => {
    fetchFaqs();
  }, []);

  const fetchFaqs = async () => {
    try {
      const res = await fetch(`${API}/faqs`);
      if (res.ok) setFaqs(await res.json());
    } catch (e) { console.error(e); }
  };

  const toggleItem = (faqId) => {
    setOpenItems(prev => ({ ...prev, [faqId]: !prev[faqId] }));
  };

  const toggleCategory = (cat) => {
    setOpenCategories(prev => ({ ...prev, [cat]: !prev[cat] }));
  };

  // Group FAQs by category
  const grouped = faqs.reduce((acc, faq) => {
    if (!acc[faq.category]) acc[faq.category] = [];
    acc[faq.category].push(faq);
    return acc;
  }, {});

  // Filter by search
  const filteredCategories = Object.entries(grouped).filter(([cat, items]) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return cat.toLowerCase().includes(q) || 
      items.some(f => f.question.toLowerCase().includes(q) || f.answer.toLowerCase().includes(q));
  });

  const filteredFaqsInCategory = (items) => {
    if (!search) return items;
    const q = search.toLowerCase();
    return items.filter(f => f.question.toLowerCase().includes(q) || f.answer.toLowerCase().includes(q));
  };

  return (
    <LightPageLayout
      title="Frequently Asked Questions"
      subtitle="Everything you need to know about Aspiritech Academy programs."
    >
      <div className="max-w-4xl mx-auto space-y-8" data-testid="faq-page">
        {/* Search */}
        <div className="relative">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search FAQs... (e.g., 'cost', 'cybersecurity', 'remote')"
            className="pl-12 h-12 text-base input-base rounded-xl"
            data-testid="faq-search"
          />
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {Object.keys(CATEGORY_ICONS).map(cat => {
            const Icon = CATEGORY_ICONS[cat];
            const count = grouped[cat]?.length || 0;
            return (
              <button
                key={cat}
                onClick={() => {
                  setOpenCategories(prev => ({ ...prev, [cat]: true }));
                  document.getElementById(`cat-${cat.replace(/[^a-zA-Z]/g, '')}`)?.scrollIntoView({ behavior: 'smooth' });
                }}
                className="flex items-center gap-2 p-3 rounded-lg bg-white border border-slate-200 hover:border-[#FFD507]/40 transition-colors text-left"
              >
                <div className={`w-8 h-8 rounded-lg bg-gradient-to-br ${CATEGORY_COLORS[cat]} flex items-center justify-center flex-shrink-0`}>
                  <Icon className="w-4 h-4 text-white" />
                </div>
                <div className="min-w-0">
                  <p className="text-xs font-medium text-slate-700 truncate">{cat}</p>
                  <p className="text-[10px] text-slate-400">{count} questions</p>
                </div>
              </button>
            );
          })}
        </div>

        {/* FAQ Categories */}
        {filteredCategories.length === 0 && (
          <Card className="card-light">
            <CardContent className="py-12 text-center">
              <HelpCircle className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500">No FAQs match your search. Try different keywords!</p>
            </CardContent>
          </Card>
        )}

        {filteredCategories.map(([category, items]) => {
          const Icon = CATEGORY_ICONS[category] || HelpCircle;
          const color = CATEGORY_COLORS[category] || "from-slate-500 to-slate-600";
          const filteredItems = filteredFaqsInCategory(items);
          const isOpen = openCategories[category] !== false; // default open

          return (
            <div key={category} id={`cat-${category.replace(/[^a-zA-Z]/g, '')}`}>
              <Card className="card-light overflow-hidden">
                <button
                  onClick={() => toggleCategory(category)}
                  className={`w-full bg-gradient-to-r ${color} p-5 text-white flex items-center justify-between`}
                  data-testid={`faq-category-${category.replace(/[^a-zA-Z]/g, '')}`}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 bg-white/20 rounded-full flex items-center justify-center">
                      <Icon className="w-5 h-5" />
                    </div>
                    <div className="text-left">
                      <h2 className="font-heading font-bold text-base">{category}</h2>
                      <p className="text-white/70 text-xs">{filteredItems.length} question{filteredItems.length !== 1 ? 's' : ''}</p>
                    </div>
                  </div>
                  <ChevronDown className={`w-5 h-5 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                </button>

                {isOpen && (
                  <CardContent className="divide-y divide-slate-100 p-0">
                    {filteredItems.map((faq) => (
                      <div key={faq.faq_id} data-testid={`faq-item-${faq.faq_id}`}>
                        <button
                          onClick={() => toggleItem(faq.faq_id)}
                          className="w-full text-left px-6 py-4 flex items-start justify-between gap-4 hover:bg-slate-50/50 transition-colors"
                        >
                          <span className="font-medium text-sm text-slate-800 leading-relaxed">{faq.question}</span>
                          <ChevronDown className={`w-4 h-4 text-slate-400 flex-shrink-0 mt-1 transition-transform ${openItems[faq.faq_id] ? 'rotate-180' : ''}`} />
                        </button>
                        {openItems[faq.faq_id] && (
                          <div className="px-6 pb-5 -mt-1">
                            <p className="text-sm text-slate-600 leading-relaxed whitespace-pre-line">{faq.answer}</p>
                          </div>
                        )}
                      </div>
                    ))}
                  </CardContent>
                )}
              </Card>
            </div>
          );
        })}

        {/* Contact CTA */}
        <Card className="card-light overflow-hidden">
          <div className="bg-gradient-to-r from-[#1E3A8A] to-[#0F1B3D] p-8 text-center text-white">
            <Rocket className="w-10 h-10 mx-auto mb-3 text-brand-gold" />
            <h3 className="text-lg font-heading font-bold mb-2">Still have questions?</h3>
            <p className="text-white/70 text-sm mb-4">We're here to help! Reach out and we'll get back to you quickly.</p>
            <a
              href="mailto:academy@aspiritech.org"
              className="inline-flex items-center gap-2 px-6 py-2.5 bg-[#FFD507] text-[#0F1B3D] rounded-lg font-semibold text-sm hover:bg-[#FFEC50] transition-colors"
            >
              Email academy@aspiritech.org
            </a>
          </div>
        </Card>
      </div>
    </LightPageLayout>
  );
};

export default FAQPage;
